declare const chrome: any;
import React, { useCallback, useContext, useRef, useState } from "react";
import { UseFormReturn, useController } from "react-hook-form";
import { ConfigFormRendererContext } from "@/components/Form/ConfigFormRenderer";
import { PlaybookEditorContext } from "./playbookEditorContext";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/shadcn/button";
import { Card } from "@/components/shadcn/card";
import {
  Plus,
  FileCode,
  MousePointer,
  List,
  CodeXml,
  Terminal,
  Copy,
  Circle,
} from "lucide-react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/shadcn/tabs";
import { cn } from "@/lib/utils";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { StepCard } from "./StepCard";
import { collectStepOutputNames } from "./stepOutputs";
import { AddStepModal } from "./AddStepModal";
import { ConvertPlaywrightModal } from "./ConvertPlaywrightModal";
import { RecordModeModal, type BrowserConfig } from "./RecordModeModal";

interface PlaybookEditorProps {
  name: string;
  form: UseFormReturn<any>;
  label?: string;
  show_recorder_button?: boolean;
}

export function PlaybookEditor({
  name,
  form,
  label = "Playbook",
  show_recorder_button = true,
}: PlaybookEditorProps) {
  const [isAddStepModalOpen, setIsAddStepModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"visual" | "json" | "script">(
    "visual"
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState<string>("");

  // Get playbook data from form
  const playbook = form.watch(name) || {
    version: "1.0",
    name: "New Playbook",
    description: "",
    variables: {},
    steps: [],
  };

  const steps = playbook.steps || [];
  const variables = playbook.variables || {};

  // Derive sibling form field paths (node config fields live alongside the
  // playbook field — e.g. `{nodeId}.stealth_mode`, `{nodeId}.use_proxy`).
  const formPrefix = name.includes(".")
    ? name.substring(0, name.lastIndexOf("."))
    : "";
  const sibling = (fieldKey: string) =>
    formPrefix ? `${formPrefix}.${fieldKey}` : fieldKey;
  const inputVarsFieldKey = sibling("input_variables");

  // In the V2 panel the form prefix is the canvas node id.
  const { isEditorV2 } = useContext(ConfigFormRendererContext);
  const v2NodeId = isEditorV2 && formPrefix ? formPrefix : null;

  // Browser environment config — read from node form so the recording
  // session inherits the same environment as node execution.  `?? default`
  // handles v3 nodes and partial configs gracefully.
  const customWindowSize = !!form.watch(sibling("custom_window_size"));
  const windowSize = form.watch(sibling("window_size")) as
    | { width?: number; height?: number }
    | undefined;
  const browserConfig: BrowserConfig = {
    stealth_mode: !!form.watch(sibling("stealth_mode")),
    use_proxy: !!form.watch(sibling("use_proxy")),
    proxy_region:
      (form.watch(sibling("proxy_region")) as string | undefined) ?? "auto",
    use_cloud_browser: !!form.watch(sibling("use_cloud_browser")),
    viewport_width:
      customWindowSize && windowSize?.width ? windowSize.width : 1280,
    viewport_height:
      customWindowSize && windowSize?.height ? windowSize.height : 720,
    disable_local_protection: !!form.watch(sibling("disable_local_protection")),
  };
  const inputVarsRaw = form.watch(inputVarsFieldKey) as
    | Array<{
        Name?: string;
        label?: string;
        name?: string;
        Type?: string;
        value?: string;
      }>
    | undefined;
  const inputVariableNames = (inputVarsRaw ?? [])
    .map((v) => v.Name || v.label || v.name)
    .filter(Boolean) as string[];
  const listInputVariableNames = (inputVarsRaw ?? [])
    .filter((v) => (v.Type || v.value) === "List of Text")
    .map((v) => v.Name || v.label || v.name)
    .filter(Boolean) as string[];

  const { field } = useController({ name, control: form.control });

  // Emit through field.onChange, not form.setValue: the V1 editor's form
  // watcher drops typeless programmatic setValue events, so setValue never
  // marks the flow dirty and playbook edits would not autosave.
  const updatePlaybook = (updates: Partial<typeof playbook>) => {
    field.onChange({ ...playbook, ...updates });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((step: any) => step.id === active.id);
      const newIndex = steps.findIndex((step: any) => step.id === over.id);

      const newSteps = arrayMove(steps, oldIndex, newIndex);
      updatePlaybook({ steps: newSteps });
    }
  };

  // Add step
  const handleAddStep = (step: any) => {
    const newSteps = [...steps, step];
    updatePlaybook({ steps: newSteps });
    setIsAddStepModalOpen(false);
  };

  // Update step
  const handleUpdateStep = (stepId: string, updates: any) => {
    const newSteps = steps.map((step: any) =>
      step.id === stepId ? { ...step, ...updates } : step
    );
    updatePlaybook({ steps: newSteps });
  };

  // Delete step
  const handleDeleteStep = (stepId: string) => {
    const newSteps = steps.filter((step: any) => step.id !== stepId);
    updatePlaybook({ steps: newSteps });
  };

  // Replace step (for placeholder → real step conversion)
  const handleReplaceStep = (stepId: string, newStep: any) => {
    const newSteps = steps.map((step: any) =>
      step.id === stepId ? { ...newStep, id: stepId } : step
    );
    updatePlaybook({ steps: newSteps });
  };

  // Handle view mode switch — sync JSON text when entering JSON mode
  const handleViewModeChange = (mode: "visual" | "json" | "script") => {
    if (mode === "json") {
      setJsonText(JSON.stringify(playbook, null, 2));
      setJsonError(null);
    }
    // Don't allow switching away from JSON if it's invalid
    if (viewMode === "json" && mode !== "json" && jsonError) return;
    setViewMode(mode);
  };

  // Handle JSON text edits — validate and sync to form when valid
  const handleJsonChange = (jsonString: string) => {
    setJsonText(jsonString);
    try {
      const parsed = JSON.parse(jsonString);
      updatePlaybook(parsed);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : "Invalid JSON");
    }
  };

  const showInstallInstructions = () => {
    alert(
      "Browser Automation Recorder Extension\n\n" +
        "To record browser actions:\n\n" +
        "1. Open chrome://extensions/\n" +
        "2. Enable 'Developer mode'\n" +
        "3. Click 'Load unpacked'\n" +
        "4. Select the browser-extension folder\n" +
        "5. Click the extension icon in your toolbar to start recording\n\n" +
        "After recording, the playbook will be copied to your clipboard."
    );
  };

  return (
    <PlaybookEditorContext.Provider
      value={{ v2NodeId, listInputVariableNames }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="space-y-3 mb-4">
          <h3 className="text-base font-medium">{label}</h3>
          <div className="flex items-center justify-between">
            <Tabs
              value={viewMode}
              onValueChange={(v) =>
                handleViewModeChange(v as "visual" | "json" | "script")
              }
              className="w-auto"
            >
              <TabsList className="w-auto">
                <TabsTrigger value="visual" className="gap-1.5">
                  <List size={14} />
                  Visual
                </TabsTrigger>
                <TabsTrigger value="json" className="gap-1.5">
                  <CodeXml size={14} />
                  JSON
                </TabsTrigger>
                <TabsTrigger value="script" className="gap-1.5">
                  <Terminal size={14} />
                  Script
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              {show_recorder_button && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRecordModalOpen(true)}
                  title="Open the live browser studio"
                >
                  <Circle className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                  Record
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsConvertModalOpen(true)}
              >
                <FileCode className="h-3.5 w-3.5 mr-1.5" />
                Import
              </Button>
            </div>
          </div>
        </div>

        {/* Content: Script, JSON, or Visual */}
        {viewMode === "script" ? (
          <PlaywrightScriptView steps={steps} />
        ) : viewMode === "json" ? (
          <div className="space-y-2">
            <CodeEditor
              value={jsonText}
              onChange={handleJsonChange}
              hasError={!!jsonError}
              placeholder="Paste your recorded playbook JSON here..."
            />
            {jsonError && (
              <div className="px-3 py-2 rounded-md bg-destructive/5 border border-destructive/20">
                <p className="text-xs text-destructive/80">
                  <span className="font-medium text-destructive">
                    Parse error:{" "}
                  </span>
                  {jsonError}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Steps */}
            {steps.length === 0 ? (
              <div className="border border-border rounded-lg py-10">
                <NoxusEmptyBlock
                  icon={MousePointer}
                  iconBackground
                  title="No steps yet"
                  description="Add steps manually or record them by interacting with a real website in the browser studio."
                  button="Add Step"
                  buttonIcon={Plus}
                  onClick={() => setIsAddStepModalOpen(true)}
                  secondaryButton="Record"
                  secondaryButtonIcon={Circle}
                  secondaryOnClick={() => setIsRecordModalOpen(true)}
                  size="sm"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={steps.map((step: any) => step.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {steps.map((step: any, index: number) => {
                      // Collect output names from all preceding steps
                      const priorOutputs: string[] = [];
                      for (let i = 0; i < index; i++) {
                        priorOutputs.push(
                          ...collectStepOutputNames([steps[i]])
                        );
                      }
                      const availableVars = [
                        ...inputVariableNames,
                        ...priorOutputs,
                      ];

                      return (
                        <StepCard
                          key={step.id}
                          id={step.id}
                          index={index}
                          step={step}
                          onUpdate={(updates) =>
                            handleUpdateStep(step.id, updates)
                          }
                          onDelete={() => handleDeleteStep(step.id)}
                          onReplace={(newStep) =>
                            handleReplaceStep(step.id, newStep)
                          }
                          inputVariableNames={availableVars}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsAddStepModalOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Step
                </Button>
              </div>
            )}

            {/* Add Step Modal */}
            <AddStepModal
              open={isAddStepModalOpen}
              onClose={() => setIsAddStepModalOpen(false)}
              onAdd={handleAddStep}
            />
          </>
        )}

        {/* Convert Playwright Modal — always rendered so it works from any tab */}
        <ConvertPlaywrightModal
          open={isConvertModalOpen}
          onClose={() => setIsConvertModalOpen(false)}
          onConvert={(convertedPlaybook) => {
            updatePlaybook(convertedPlaybook);
          }}
        />

        {/* Record Mode Modal */}
        <RecordModeModal
          open={isRecordModalOpen}
          onClose={() => setIsRecordModalOpen(false)}
          initialSteps={steps}
          onSave={(newSteps) => updatePlaybook({ steps: newSteps })}
          browserConfig={browserConfig}
        />
      </div>
    </PlaybookEditorContext.Provider>
  );
}

// ── Code Editor ─────────────────────────────────────────────────────

function CodeEditor({
  value,
  onChange,
  hasError,
  placeholder,
  readOnly,
}: {
  value: string;
  onChange?: (value: string) => void;
  hasError?: boolean;
  placeholder?: string;
  readOnly?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const lines = value.split("\n");
  const lineCount = lines.length || 1;

  const syncScroll = () => {
    if (textareaRef.current && gutterRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = textareaRef.current;
      if (!ta || readOnly) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = value.substring(0, start) + "  " + value.substring(end);
      onChange?.(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  };

  return (
    <div
      className={cn(
        "relative flex rounded-lg border bg-muted/30 overflow-hidden max-h-[400px] transition-colors",
        hasError ? "border-destructive" : "border-border",
        !readOnly && "focus-within:ring-1",
        !readOnly &&
          (hasError
            ? "focus-within:ring-destructive/30"
            : "focus-within:ring-ring")
      )}
    >
      {/* Line numbers gutter */}
      <div
        ref={gutterRef}
        className="shrink-0 overflow-hidden select-none pointer-events-none py-3 pl-3 pr-2 border-r border-border/60 bg-muted/50"
        aria-hidden
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div
            key={i}
            className="text-[11px] leading-[1.35rem] font-mono text-muted-foreground/40 text-right tabular-nums"
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Editor area */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onScroll={syncScroll}
        onKeyDown={handleKeyDown}
        readOnly={readOnly}
        placeholder={placeholder}
        spellCheck={false}
        className={cn(
          "flex-1 resize-none bg-transparent p-3 font-mono text-[12.5px] leading-[1.35rem] text-foreground",
          "outline-none border-none shadow-none focus-visible:ring-0",
          "min-h-[200px] overflow-auto",
          "placeholder:text-muted-foreground/40",
          readOnly && "cursor-default"
        )}
      />
    </div>
  );
}

// ── Playwright Script View ──────────────────────────────────────────

function selectorToLocator(sel: any): string {
  if (!sel) return 'page.locator("TODO")';
  if (sel.role) {
    const opts: string[] = [];
    if (sel.role_name) opts.push(`name: ${JSON.stringify(sel.role_name)}`);
    if (sel.exact) opts.push("exact: true");
    const optsStr = opts.length ? `, { ${opts.join(", ")} }` : "";
    return `page.getByRole(${JSON.stringify(sel.role)}${optsStr})`;
  }
  if (sel.text)
    return `page.getByText(${JSON.stringify(sel.text)}${sel.exact ? ", { exact: true }" : ""})`;
  if (sel.label)
    return `page.getByLabel(${JSON.stringify(sel.label)}${sel.exact ? ", { exact: true }" : ""})`;
  if (sel.placeholder)
    return `page.getByPlaceholder(${JSON.stringify(sel.placeholder)})`;
  if (sel.test_id) return `page.getByTestId(${JSON.stringify(sel.test_id)})`;
  if (sel.primary) return `page.locator(${JSON.stringify(sel.primary)})`;
  return 'page.locator("TODO")';
}

function stepToScript(step: any, indent: string = ""): string[] {
  const loc = () => selectorToLocator(step.selector);
  switch (step.type) {
    case "navigate":
      return [`${indent}await page.goto(${JSON.stringify(step.url || "")});`];
    case "reload":
      return [`${indent}await page.reload();`];
    case "click":
      return [`${indent}await ${loc()}.click();`];
    case "type":
      return [
        ...(step.clear_first ? [`${indent}await ${loc()}.clear();`] : []),
        `${indent}await ${loc()}.pressSequentially(${JSON.stringify(step.text || "")});`,
      ];
    case "fill":
      return [
        `${indent}await ${loc()}.fill(${JSON.stringify(step.value || "")});`,
      ];
    case "select": {
      const by = step.by || "value";
      const raw = step.value;
      const arg = Array.isArray(raw)
        ? by === "index"
          ? `{ index: [${raw.map((v: string) => Number(v) || 0).join(", ")}] }`
          : `{ ${by}: ${JSON.stringify(raw)} }`
        : by === "index"
          ? `{ index: ${Number(raw) || 0} }`
          : by === "value"
            ? JSON.stringify(raw || "")
            : `{ ${by}: ${JSON.stringify(raw || "")} }`;
      return [`${indent}await ${loc()}.selectOption(${arg});`];
    }
    case "press":
      return [
        `${indent}await page.keyboard.press(${JSON.stringify(step.key || "Enter")});`,
      ];
    case "upload":
      return [
        `${indent}await ${loc()}.setInputFiles(${JSON.stringify(
          step.file_variable || ""
        )});`,
      ];
    case "extract": {
      if (step.whole_page) {
        const pageMethod =
          step.extract_type === "html" ? "innerHTML" : "innerText";
        return [
          `${indent}const ${step.name || "data"} = await page.locator("body").${pageMethod}();`,
        ];
      }
      if (step.extract_type === "attribute") {
        return [
          `${indent}const ${step.name || "data"} = await ${loc()}.getAttribute(${JSON.stringify(step.attribute || "")});`,
        ];
      }
      const method =
        step.extract_type === "html"
          ? "innerHTML"
          : step.extract_type === "value"
            ? "inputValue"
            : "textContent";
      return [
        `${indent}const ${step.name || "data"} = await ${loc()}.${method}();`,
      ];
    }
    case "screenshot":
      return [
        `${indent}const ${step.name || "screenshot"} = await page.screenshot(${step.full_page ? "{ fullPage: true }" : ""});`,
      ];
    case "download":
      if (step.url)
        return [
          `${indent}const ${step.name || "download"} = await page.goto(${JSON.stringify(step.url)});`,
        ];
      return [
        `${indent}const [${step.name || "download"}] = await Promise.all([`,
        `${indent}  page.waitForEvent("download"),`,
        `${indent}  ${selectorToLocator(step.trigger_selector)}.click(),`,
        `${indent}]);`,
      ];
    case "extract_cookies":
      return [
        `${indent}const ${step.name || "cookies"} = await context.cookies(${step.domain ? JSON.stringify(step.domain) : ""});`,
      ];
    case "extract_request_headers":
      return [
        `${indent}// Extract request headers${step.url_pattern ? ` matching ${step.url_pattern}` : ""}`,
      ];
    case "extract_url":
      return [`${indent}const ${step.name || "current_url"} = page.url();`];
    case "set_cookies": {
      const inline = Array.isArray(step.cookies) ? step.cookies : [];
      const lines: string[] = [];
      if (inline.length)
        lines.push(
          `${indent}await context.addCookies(${JSON.stringify(inline)});`
        );
      if (step.from_variable)
        lines.push(
          `${indent}// plus cookies from variable: ${step.from_variable}`
        );
      return lines.length
        ? lines
        : [`${indent}// set_cookies (no cookies configured)`];
    }
    case "clear_cookies":
      return [
        `${indent}await context.clearCookies(${
          step.domain ? `{ domain: ${JSON.stringify(step.domain)} }` : ""
        });`,
      ];
    case "wait_for":
      return [
        `${indent}await ${loc()}.waitFor({ state: ${JSON.stringify(step.state || "visible")} });`,
      ];
    case "wait_for_timeout":
      return [`${indent}await page.waitForTimeout(${step.timeout || 1000});`];
    case "wait_for_load":
      return [
        `${indent}await page.waitForLoadState(${JSON.stringify(step.wait_until || "load")});`,
      ];
    case "scroll":
      if (step.to === "element")
        return [
          `${indent}await ${selectorToLocator(step.selector)}.scrollIntoViewIfNeeded();`,
        ];
      return [
        `${indent}await page.evaluate(() => window.scrollTo(0, ${step.to === "top" ? "0" : "document.body.scrollHeight"}));`,
      ];
    case "if": {
      const lines = [
        `${indent}if (await ${selectorToLocator(step.condition?.selector)}.isVisible()) {`,
      ];
      for (const s of step.then || [])
        lines.push(...stepToScript(s, indent + "  "));
      if (step.otherwise?.length) {
        lines.push(`${indent}} else {`);
        for (const s of step.otherwise)
          lines.push(...stepToScript(s, indent + "  "));
      }
      lines.push(`${indent}}`);
      return lines;
    }
    case "loop": {
      const lines =
        step.over === "list"
          ? [
              `${indent}for (const loop_item of ${JSON.stringify(step.items || [])}) {`,
            ]
          : [
              `${indent}for (let i = 0; i < ${step.count || step.max_iterations || 5}; i++) {`,
            ];
      for (const s of step.steps || [])
        lines.push(...stepToScript(s, indent + "  "));
      lines.push(`${indent}}`);
      return lines;
    }
    case "set_variable":
      return [
        `${indent}const ${step.name || "variable"} = ${JSON.stringify(step.value || "")};`,
      ];
    case "agent":
      return [`${indent}// AI Agent: ${step.prompt || "..."}`];
    case "run_script": {
      const outputs =
        Array.isArray(step.outputs) && step.outputs.length
          ? step.outputs.join(", ")
          : "none";
      const body = String(step.script ?? "")
        .split("\n")
        .map((line) => `${indent}${line.replace(/\*\//g, "*\\/")}`);
      return [
        `${indent}// Run Python Playwright script (outputs: ${outputs})`,
        `${indent}/*`,
        ...body,
        `${indent}*/`,
      ];
    }
    case "placeholder":
      return [
        `${indent}// TODO: ${step.original_code || step.description || "Configure this step manually"}`,
      ];
    default:
      return [`${indent}// Unknown step: ${step.type}`];
  }
}

function generateScript(steps: any[]): string {
  const lines: string[] = [];
  for (const step of steps) {
    if (step.description) lines.push(`// ${step.description}`);
    lines.push(...stepToScript(step));
    lines.push("");
  }
  // Remove trailing blank line
  while (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines.join("\n");
}

function PlaywrightScriptView({ steps }: { steps: any[] }) {
  const script = generateScript(steps);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className="absolute top-2 right-3 z-10 h-7 text-xs"
      >
        <Copy className="h-3 w-3 mr-1" />
        {copied ? "Copied" : "Copy"}
      </Button>
      <CodeEditor value={script} readOnly />
    </div>
  );
}
